import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import {
  getLocalStorageJson,
  removeItemFromLocalStorage,
  setLocalStorageJson,
} from "@redotech/redo-web/utils/local-storage-wrapper";

interface CacheData {
  sessionId: string;
  viewedProducts: SaveForLaterProductData[];
  hasShownSlideInPanel: boolean;
}

export interface SaveForLaterProductData {
  productId: string;
  productHandle: string;
  productVariantId: string | undefined;
  productImgSrc?: string | undefined;
}

class _SaveForLaterCache {
  static STORAGE_KEY = "redo.save_for_later_cache";

  shouldTrackSaveForLater(): boolean {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );
    const shopifySessionId = getCookie(AllCookies.ShopifySessionId);

    return (
      !cacheData ||
      cacheData.sessionId !== shopifySessionId ||
      !cacheData.hasShownSlideInPanel
    );
  }

  hasShownSlideInPanel(): boolean {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );

    return !!cacheData?.hasShownSlideInPanel;
  }

  handleShowSlideInPanel(): void {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );

    if (cacheData) {
      const updatedCacheData: CacheData = {
        ...cacheData,
        hasShownSlideInPanel: true,
      };
      setLocalStorageJson(_SaveForLaterCache.STORAGE_KEY, updatedCacheData);
    }
  }

  getAllProductsToSaveForLater(): SaveForLaterProductData[] {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );

    if (!cacheData?.viewedProducts) {
      return [];
    }

    return cacheData.viewedProducts;
  }

  /**
   * Adds a product to the save for later cache if a product with the same id
   * is not already in the cache.
   */
  addProductToSaveForLaterCache(
    product: SaveForLaterProductData,
  ): SaveForLaterProductData[] {
    const cacheData: CacheData | null = getLocalStorageJson(
      _SaveForLaterCache.STORAGE_KEY,
    );
    const shopifySessionId = getCookie(AllCookies.ShopifySessionId);

    if (this.shouldTrackSaveForLater()) {
      if (!cacheData || cacheData.sessionId !== shopifySessionId) {
        // If there is no cache or the user is in a new session, create a new cache.
        setLocalStorageJson(_SaveForLaterCache.STORAGE_KEY, {
          sessionId: shopifySessionId,
          viewedProducts: [product],
          hasShownSlideInPanel: false,
        });
      } else if (
        !cacheData.viewedProducts.some((p) => p.productId === product.productId)
      ) {
        // Otherwise, if the product (excluding variants) is not already in the cache,
        // add the product to the cache.
        const updatedCacheData: CacheData = {
          sessionId: cacheData.sessionId,
          viewedProducts: [...cacheData.viewedProducts, product],
          hasShownSlideInPanel: false,
        };
        setLocalStorageJson(_SaveForLaterCache.STORAGE_KEY, updatedCacheData);
      }
    }

    return this.getAllProductsToSaveForLater();
  }

  clear() {
    removeItemFromLocalStorage(_SaveForLaterCache.STORAGE_KEY);
  }
}

export const SaveForLaterCache = new _SaveForLaterCache();
